<script>
import { LabeledInput } from '@components/Form/LabeledInput';
import { Checkbox } from '@components/Form/Checkbox';
import FileSelectorTextArea from '@shell/components/form/FileSelectorTextArea.vue';
import ResourceLabeledSelect from '@shell/components/form/ResourceLabeledSelect';
import { mapGetters } from 'vuex';
import { SECRET } from '@shell/config/types';

export default {
  emits: ['valid'],

  components: {
    LabeledInput,
    Checkbox,
    FileSelectorTextArea,
    ResourceLabeledSelect,
  },

  props: {
    value: {
      type:    Object,
      default: () => {
        return {};
      }
    },
    mode: {
      type:    String,
      default: 'create'
    },

  },

  data() {
    return { SECRET };
  },

  mounted() {
    this.$emit('valid', this.valid);
  },

  beforeUnmount() {
    this.$emit('valid', true);
  },

  watch: {
    valid() {
      this.$emit('valid', this.valid);
    }
  },

  computed: {
    credentialSecret: {
      get() {
        const { credentialSecretName, credentialSecretNamespace } = this.value;

        return { metadata: { name: credentialSecretName, namespace: credentialSecretNamespace } };
      },

      set(neu) {
        const { name, namespace } = neu.metadata;

        this.value['credentialSecretName'] = name;
        this.value['credentialSecretNamespace'] = namespace;
      }
    },
    valid() {
      return !!this.value.endpoint && !!this.value.bucketName;
    },
    ...mapGetters({ t: 'i18n/t' })
  },

  methods: {
    // The chart expects the CA base64-encoded, so a file's contents are encoded on the way in.
    // btoa throws on characters above U+00FF; letting that through leaves the field untouched and
    // growls, rather than silently storing text the chart cannot decode.
    encodeCA(ca) {
      return btoa(ca);
    },
  },

  created() {
    const { credentialSecretName, credentialSecretNamespace } = this.value;

    if (credentialSecretName && !credentialSecretNamespace) {
      this.value.credentialSecretName = '';
    }
  },
};
</script>

<template>
  <div>
    <div class="row mb-10">
      <div class="col span-6">
        <ResourceLabeledSelect
          v-model:value="credentialSecret"
          :get-option-label="opt=>opt.metadata.name || ''"
          option-key="id"
          :mode="mode"
          :label="t('backupRestoreOperator.s3.credentialSecretName')"
          :resource-type="SECRET"
        />
      </div>
      <div class="col span-6">
        <LabeledInput
          v-model:value="value.bucketName"
          data-testid="S3-bucketName"
          :mode="mode"
          :label="t('backupRestoreOperator.s3.bucketName')"
          required
        />
      </div>
    </div>
    <div class="row mb-10">
      <div class="col span-6">
        <LabeledInput
          v-model:value="value.region"
          :mode="mode"
          :label="t('backupRestoreOperator.s3.region')"
        />
      </div>
      <div class="col span-6">
        <LabeledInput
          v-model:value="value.folder"
          :mode="mode"
          :label="t('backupRestoreOperator.s3.folder')"
        />
      </div>
    </div>
    <div class="row mb-10">
      <div class="col span-6">
        <LabeledInput
          v-model:value="value.endpoint"
          :mode="mode"
          :label="t('backupRestoreOperator.s3.endpoint')"
          data-testid="S3-endpoint"
          required
        />
        <Checkbox
          v-model:value="value.insecureTLSSkipVerify"
          class="mt-10"
          :mode="mode"
          :label="t('backupRestoreOperator.s3.insecureTLSSkipVerify')"
        />
      </div>
    </div>
    <div class="row mb-10">
      <div class="col span-12">
        <FileSelectorTextArea
          v-model:value="value.endpointCA"
          :mode="mode"
          :label="t('backupRestoreOperator.s3.endpointCA.label')"
          :tooltip="t('backupRestoreOperator.s3.endpointCA.prompt')"
          :transform-file="encodeCA"
        />
      </div>
    </div>
  </div>
</template>
